import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap } from 'lucide-react';
import { Product } from './FightCardProduct';
interface LimitedDropsProps {
  onAddToCart: (product: Product) => void;
}
const DROP_PRODUCTS: Product[] = [{
  id: 'drop-1',
  name: 'MIDNIGHT STRIKE GLOVES',
  price: 120,
  category: 'GEAR',
  image: 'https://images.unsplash.com/photo-1595078475328-1ab05d0a6a0e?q=80&w=1000&auto=format&fit=crop',
  isLimited: true,
  badge: 'ONLY 12 LEFT'
}, {
  id: 'drop-2',
  name: 'CHAMPIONSHIP BELT HOODIE',
  price: 150,
  category: 'HOODIES',
  image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?q=80&w=1000&auto=format&fit=crop',
  isLimited: true,
  badge: 'ONLY 5 LEFT'
}];
export function LimitedDrops({
  onAddToCart
}: LimitedDropsProps) {
  const [timeLeft, setTimeLeft] = useState({
    days: 3,
    hours: 12,
    minutes: 45,
    seconds: 0
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          ...prev,
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        if (prev.days > 0) return {
          ...prev,
          days: prev.days - 1,
          hours: 23,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return <section id="drops" className="py-24 relative overflow-hidden" style={{
    background: '#0d0d0d'
  }} data-id="element-120">
      {/* Atmospheric red glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full pointer-events-none" style={{
      background: 'radial-gradient(ellipse at 50% 0%, rgba(153,27,27,0.08) 0%, transparent 60%)'
    }} data-id="element-121" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10" data-id="element-122">
        <div className="flex flex-col lg:flex-row gap-16 items-center" data-id="element-123">
          {/* Info Side */}
          <div className="lg:w-1/3 text-center lg:text-left" data-id="element-124">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{
            background: 'rgba(245,230,20,0.05)',
            border: '1px solid rgba(245,230,20,0.2)',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 0 20px rgba(245,230,20,0.05)'
          }} data-id="element-125">
              <Zap className="w-4 h-4 animate-pulse" style={{
              color: 'rgba(245,230,20,0.8)',
              fill: 'rgba(245,230,20,0.8)'
            }} data-id="element-126" />
              <span className="font-bold font-barlow tracking-widest text-sm yellow-glow-text" data-id="element-127">
                DROPS LIVE NOW
              </span>
            </div>

            <h2 className="text-6xl md:text-8xl font-bebas leading-none mb-6" style={{
            color: '#e0e0e0'
          }} data-id="element-128">
              LIMITED
              <br data-id="element-129" />
              <span style={{
              color: 'rgba(153,27,27,0.9)',
              textShadow: '0 0 30px rgba(153,27,27,0.3)'
            }} data-id="element-130">
                EDITION
              </span>
            </h2>

            <p className="font-barlow text-xl mb-10 max-w-md mx-auto lg:mx-0" style={{
            color: '#555'
          }} data-id="element-131">
              Exclusive gear. Once they're gone, they're gone forever. Secure
              your piece of history.
            </p>

            {/* Glass Timer */}
            <div className="flex justify-center lg:justify-start gap-3 mb-10" data-id="element-132">
              {Object.entries(timeLeft).map(([unit, value]) => <div key={unit} className="flex flex-col items-center" data-id="element-133">
                  <div className="w-18 h-20 flex items-center justify-center mb-2 relative overflow-hidden" style={{
                width: '72px',
                height: '80px',
                background: 'rgba(255,255,255,0.03)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(245,230,20,0.15)',
                boxShadow: '0 0 20px rgba(245,230,20,0.04), inset 0 1px 0 rgba(255,255,255,0.06)'
              }} data-id="element-134">
                    <AnimatePresence mode="popLayout" data-id="element-135">
                      <motion.span key={value} initial={{
                    y: 16,
                    opacity: 0
                  }} animate={{
                    y: 0,
                    opacity: 1
                  }} exit={{
                    y: -16,
                    opacity: 0
                  }} className="text-4xl font-bebas absolute yellow-glow-text" data-id="element-136">
                        {String(value).padStart(2, '0')}
                      </motion.span>
                    </AnimatePresence>
                  </div>
                  <span className="text-xs font-barlow uppercase tracking-widest" style={{
                color: '#555'
              }} data-id="element-137">
                    {unit}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Drop Product Cards */}
          <div className="lg:w-2/3 grid grid-cols-1 md:grid-cols-2 gap-8 w-full" data-id="element-138">
            {DROP_PRODUCTS.map((product, idx) => <motion.div key={product.id} initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: idx * 0.2
          }} viewport={{
            once: true
          }} className="group relative overflow-hidden aspect-[4/5]" style={{
            background: 'rgba(255,255,255,0.02)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(153,27,27,0.2)',
            boxShadow: '0 0 30px rgba(153,27,27,0.06)'
          }} data-id="element-139">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover opacity-60 group-hover:opacity-75 transition-opacity duration-500" data-id="element-140" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent opacity-90" data-id="element-141" />

                {/* Hover inner glow */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{
              background: 'radial-gradient(ellipse at 50% 100%, rgba(245,230,20,0.05) 0%, transparent 60%)'
            }} data-id="element-142" />

                <div className="absolute top-4 right-4 px-3 py-1 font-bebas text-lg tracking-wide" style={{
              background: 'rgba(153,27,27,0.7)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(153,27,27,0.4)',
              color: '#e0e0e0',
              boxShadow: '0 0 16px rgba(153,27,27,0.2)'
            }} data-id="element-143">
                  {product.badge}
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-144">
                  <h3 className="text-3xl font-bebas mb-2" style={{
                color: '#e0e0e0'
              }} data-id="element-145">
                    {product.name}
                  </h3>
                  <div className="flex justify-between items-end" data-id="element-146">
                    <span className="text-2xl font-barlow font-bold yellow-glow-text" data-id="element-147">
                      ${product.price}
                    </span>
                    <button onClick={() => onAddToCart(product)} className="px-6 py-2 font-bebas text-xl transition-all duration-300" style={{
                  background: 'rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: '#e0e0e0'
                }} onMouseEnter={e => {
                  ;
                  (e.currentTarget as HTMLButtonElement).style.background = 'rgba(153,27,27,0.7)';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(153,27,27,0.4)';
                }} onMouseLeave={e => {
                  ;
                  (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.08)';
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.12)';
                }} data-id="element-148">
                      SECURE DROP
                    </button>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}